/** @file SheetOverlay: the sheet with numbered island boxes and a draggable pivot dot per island. */
import type { PointerEvent, ReactElement } from 'react';
import { useRef } from 'react';
import type { IslandTarget } from './proposeTargets';
import type { Point } from './sheetTypes';

/** The sheet (`url`, `width` x `height` px), the targets in order, and the pivot callback (island pixels). */
export type SheetOverlayProps = { url: string; width: number; height: number; targets: readonly IslandTarget[]; onPivot: (index: number, pivot: Point) => void };

/** The sheet scaled to the dialog's height; an SVG on top in sheet pixels draws the boxes, numbers and pivots. */
export function SheetOverlay({ url, width, height, targets, onPivot }: SheetOverlayProps): ReactElement {
  const svg = useRef<SVGSVGElement>(null);
  const drag = useRef<number | null>(null);
  const sheetPoint = (e: PointerEvent): Point | null => {
    const r = svg.current?.getBoundingClientRect();
    return r === undefined || r.width === 0 ? null : [((e.clientX - r.left) / r.width) * width, ((e.clientY - r.top) / r.height) * height];
  };
  const move = (e: PointerEvent): void => {
    const k = drag.current;
    const p = sheetPoint(e);
    const t = k === null ? undefined : targets[k];
    if (k === null || p === null || t === undefined) return;
    onPivot(k, [Math.min(t.island.w, Math.max(0, p[0] - t.island.x)), Math.min(t.island.h, Math.max(0, p[1] - t.island.y))]);
  };
  const stroke = Math.max(1, Math.round(Math.max(width, height) / 400));
  return (
    <div style={{ position: 'relative', height: 'min(70vh, 640px)', aspectRatio: `${String(width)} / ${String(height)}`, background: '#111', flex: 'none' }}>
      {url === '' ? null : <img src={url} alt="sheet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', imageRendering: 'pixelated' }} />}
      <svg
        ref={svg} viewBox={`0 0 ${String(width)} ${String(height)}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        onPointerMove={move} onPointerUp={() => { drag.current = null; }} onPointerLeave={() => { drag.current = null; }}
      >
        {targets.map((t, k) => (
          <g key={t.island.id} data-island={k + 1}>
            <rect x={t.island.x} y={t.island.y} width={t.island.w} height={t.island.h} fill="none" stroke={t.part === null ? '#888' : '#4cc2ff'} strokeWidth={stroke} strokeDasharray={t.part === null ? `${String(stroke * 3)} ${String(stroke * 2)}` : undefined} />
            <text x={t.island.x + stroke * 2} y={t.island.y - stroke * 2} fill="#fff" fontSize={stroke * 10} fontFamily="system-ui, sans-serif" stroke="#000" strokeWidth={stroke / 2}>{k + 1}</text>
            {t.part === null ? null : (
              <circle
                cx={t.island.x + t.pivot[0]} cy={t.island.y + t.pivot[1]} r={stroke * 3} fill="#ff4f6d" stroke="#fff" strokeWidth={stroke}
                style={{ cursor: 'grab' }} aria-label={`pivot ${String(k + 1)}`}
                onPointerDown={(e) => { drag.current = k; e.currentTarget.ownerSVGElement?.setPointerCapture(e.pointerId); }}
              />
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
